import React from 'react';
import { Box, Button, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Text } from '@chatsdet/ui';
import { Check, Copy, Warning } from '@phosphor-icons/react';
import { copyTextToClipboard } from '../clipboard-copy.js';

type Props = {
  /** The raw secret, or null when the dialog should be closed. */
  secret: string | null;
  onClose: () => void;
};

/**
 * Shown once, immediately after creating a client or rotating its secret.
 * The server never returns the secret again after this response.
 */
export function TestSignInSecretDialog({ secret, onClose }: Props) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (!secret) return;
    const ok = await copyTextToClipboard(secret);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <DialogTrigger
      isOpen={!!secret}
      onOpenChange={(open) => {
        if (!open) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <span />
      <Dialog title="Client secret">
        <DialogContent>
          <Box className="space-y-4">
            <Flex
              align="center"
              gap="sm"
              className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-300 dark:border-amber-700"
            >
              <Warning size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <Text size="sm" className="text-amber-800 dark:text-amber-200">
                Copy this secret now — you won&apos;t see it again.
              </Text>
            </Flex>
            <div className="relative">
              <code className="block p-4 bg-muted rounded-lg font-mono text-sm break-all pr-12">
                {secret}
              </code>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Copy client secret"
                className="absolute top-2 right-2"
                onPress={handleCopy}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
